import { useMemo } from 'react';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { AdsHistoryGroupedEntry } from '../components/AdsHistoryGroupedView.mockData';
import { Box, Tooltip } from '@mui/material';
import { Cancel, CheckCircle } from '@mui/icons-material';
import {
  CellContainer,
  CellTypography,
  Image,
  ImageSkeleton,
} from '../components/AdsHistoryGrid.styles';

const colOpts = { disableColumnFilter: true, disableColumnMenu: true, sortable: false } as const;

export const useAdsHistoryGroupedColumns = (): GridColDef[] =>
  useMemo(
    () => [
      {
        field: 'preview',
        headerName: 'Prev.',
        width: 68,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellContainer>
            <Box sx={{ position: 'relative' }}>
              <ImageSkeleton />
              <Image
                src={params.row.creative.preview}
                alt={params.row.creative.name}
                loading="lazy"
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  img.style.opacity = '1';
                }}
                style={{ position: 'absolute', top: 0, left: 0, opacity: 0, transition: 'opacity 0.2s ease-in-out' }}
              />
            </Box>
          </CellContainer>
        ),
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 280,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <Tooltip title={params.row.creative.name} placement="bottom">
            <CellTypography variant="body2">{params.row.creative.name}</CellTypography>
          </Tooltip>
        ),
      },
      {
        field: 'ad_id',
        headerName: 'Ad ID',
        width: 160,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2" sx={{ color: params.row.ad_id ? 'primary.main' : 'text.disabled' }}>
            {params.row.ad_id || '—'}
          </CellTypography>
        ),
      },
      {
        field: 'flow',
        headerName: 'Flow',
        width: 100,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2">{params.row.flow ?? '—'}</CellTypography>
        ),
      },
      {
        field: 'customer',
        headerName: 'Customer',
        width: 130,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2">{params.row.customer ?? '—'}</CellTypography>
        ),
      },
      {
        field: 'tpc_finish_date',
        headerName: 'TPC Finish Date',
        width: 140,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2">{params.row.tpc_finish_date ?? '—'}</CellTypography>
        ),
      },
      {
        field: 'creative_type',
        headerName: 'Type',
        width: 80,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2">{params.row.creative_type ?? '—'}</CellTypography>
        ),
      },
      {
        field: 'concept',
        headerName: 'Concept',
        width: 160,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <Tooltip title={params.row.concept ?? ''} placement="bottom">
            <CellTypography variant="body2">{params.row.concept ?? '—'}</CellTypography>
          </Tooltip>
        ),
      },
      {
        field: 'variable',
        headerName: 'Variable',
        width: 100,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2">{params.row.variable ?? '—'}</CellTypography>
        ),
      },
      {
        field: 'idea_type',
        headerName: 'Idea type',
        width: 100,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2">{params.row.idea_type ?? '—'}</CellTypography>
        ),
      },
      {
        field: 'headline',
        headerName: 'Headline',
        width: 160,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <Tooltip title={params.row.headline ?? ''} placement="bottom">
            <CellTypography variant="body2">{params.row.headline ?? '—'}</CellTypography>
          </Tooltip>
        ),
      },
      {
        field: 'technology',
        headerName: 'Technology',
        width: 100,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellTypography variant="body2">{params.row.technology ?? '—'}</CellTypography>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 70,
        ...colOpts,
        renderCell: (params: GridRenderCellParams<AdsHistoryGroupedEntry>) => (
          <CellContainer>
            {params.row.status ? (
              <CheckCircle sx={(t) => ({ color: t.palette.success.light, fontSize: t.typography.h4.fontSize })} />
            ) : (
              <Cancel sx={(t) => ({ color: t.palette.error.light, fontSize: t.typography.h4.fontSize })} />
            )}
          </CellContainer>
        ),
      },
    ],
    [],
  );

